import { UploadOutlined } from "@ant-design/icons"
import { Button, Tabs } from "antd"
import { uniq } from "lodash"
import { ReactNode, useCallback, useMemo, useState } from "react"

import { classAtoms, evaluationAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import Firebase, { NULL_ID } from "#classes/Firebase"
import { usePortfolioDbStorage } from "#components/PortfolioManagement/usePortfolioDbStorage"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { EvaluationConstructor } from "#customTypes/composedTypes/evaluationTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"
import { useFilteredDocs, useSomeDocs } from "#hooks/jotai/useSubscription"
import useHasPermission from "#hooks/newHooks/useHasPermission"

import AddStudentDetails from "./AddStudentDetails"
import ArtworkUploadModal from "./ArtworkUploadModal"
import {
  type ArtworkFieldUpdate,
  type ArtworkTile,
  buildArtworkHiddenUpdate,
  buildArtworkInPortfolioUpdate,
  getArtworkDisplayUrl,
} from "./studentArtworkTilesHelpers"
import StudentArtworkTilesSection from "./StudentArtworkTilesSection"
import StudentDocumentsSection from "./StudentDocumentsSection"
import { studentHasAssistantAttendance } from "./studentProfileTabHelpers"
import StudentProjectsSection from "./StudentProjectsSection"
import StudentSkillHistorySection from "./StudentSkillHistorySection"
import StudentStandoutMomentsTab from "./StudentStandoutMomentsTab"
import StudentTaJourneyTab from "./StudentTaJourneyTab"
import { useStudentFromForm } from "./useStudentFromForm"
import ViewPortfolioButton from "./ViewPortfolioButton"

const MAX_HEIGHT = 900

const TAB_KEYS = {
  DETAILS: "details",
  SKILL_HISTORY: "skillHistory",
  STANDOUT_MOMENTS: "standoutMoments",
  TA_JOURNEY: "taJourney",
  PROJECTS: "projects",
  ARTWORK: "artwork",
  DOCUMENTS: "documents",
} as const

type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS]

const TAB_LABELS: Record<TabKey, string> = {
  [TAB_KEYS.DETAILS]: "Details",
  [TAB_KEYS.SKILL_HISTORY]: "Skill History",
  [TAB_KEYS.STANDOUT_MOMENTS]: "Standout Moments",
  [TAB_KEYS.TA_JOURNEY]: "TA Journey",
  [TAB_KEYS.PROJECTS]: "Projects",
  [TAB_KEYS.ARTWORK]: "Artwork",
  [TAB_KEYS.DOCUMENTS]: "Documents",
}

const SkillHistoryTab = () => {
  const { studentId, student } = useStudentFromForm()

  const evalsQuery: FirebaseQueryConfig = useMemo(() => {
    return {
      queries: {
        studentId: {
          value: studentId ?? NULL_ID,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }
  }, [studentId])

  const studentEvals = useFilteredDocs<EvaluationConstructor>(
    evaluationAtoms,
    DOC_TYPES.EVALUATIONS,
    evalsQuery
  )

  return (
    <Flex col gap={30} style={{ height: MAX_HEIGHT }}>
      <StudentSkillHistorySection
        student={student}
        evals={studentEvals}
        studentId={studentId}
        isLoading={Boolean(studentId) && !student}
      />
    </Flex>
  )
}

const ProjectsTab = () => {
  const { student } = useStudentFromForm()

  return (
    <Flex col gap={30} style={{ height: MAX_HEIGHT }}>
      <StudentProjectsSection projects={student?.projects} />
    </Flex>
  )
}

const ArtworkTab = ({
  showPortfolioButton,
  onAddArtwork,
}: {
  showPortfolioButton: boolean
  onAddArtwork: (classId?: Uuid) => void
}) => {
  const { studentId, student } = useStudentFromForm()
  const artwork = student?.artwork

  /**
   * Writes `artwork.<id>.<field>` paths, so this tab's writers — a debounced
   * rotation flush, add-to-portfolio, and hide/unhide — merge with each
   * other and with an `ArtworkUploadModal` upload instead of overwriting the
   * whole map.
   * Resolves false when there is nothing to write (no student, or the target
   * piece has since left the map), so callers can skip their follow-up work.
   */
  const saveArtworkFields = useCallback(
    async (update: ArtworkFieldUpdate | null): Promise<boolean> => {
      if (!studentId || !update) return false
      await Firebase.getInstance().updateDocument(
        DOC_TYPES.STUDENTS,
        studentId,
        update
      )
      return true
    },
    [studentId]
  )

  const { createProject } = usePortfolioDbStorage({
    studentId: studentId ?? null,
  })

  // Audit stamp: always the real logged-in user, never the impersonated
  // effective user.
  const { currentUserId } = useUserContext()

  const canHideArtwork = useHasPermission(
    RESOURCES.STUDENT,
    USER_ACTIONS.HIDE_ARTWORK
  )

  const handleHideArtwork = useCallback(
    async (tile: ArtworkTile, isHidden: boolean) => {
      invariant(currentUserId != null, "Current user id is not defined!")
      await saveArtworkFields(
        buildArtworkHiddenUpdate(artwork, tile.id, isHidden, currentUserId)
      )
    },
    [artwork, currentUserId, saveArtworkFields]
  )

  // Same two-part write as the old artwork section: flag the piece
  // `inPortfolio`, then seed a portfolio project with it as Step 1.
  const handleAddToPortfolio = useCallback(
    async (tile: ArtworkTile) => {
      const saved = await saveArtworkFields(
        buildArtworkInPortfolioUpdate(artwork, tile.id)
      )
      if (!saved) return

      createProject({
        title: `New project - ${tile.className}`,
        category: "selected",
        initialStepImageUrl: getArtworkDisplayUrl(tile.artwork),
        initialStepLabel: "Step 1",
        initialStepArtworkId: tile.id,
      })
    },
    [artwork, createProject, saveArtworkFields]
  )

  return (
    // No overflow here: the tiles section scrolls its own artwork container
    // under a sticky controls header, so the upload row never scrolls away.
    <Flex col gap={16} style={{ height: MAX_HEIGHT }}>
      <Flex gap={8} style={{ alignItems: "center" }}>
        <Button
          icon={<UploadOutlined />}
          onClick={() => onAddArtwork()}
          data-testid="artwork-tab-upload"
        >
          Upload artwork
        </Button>
        {showPortfolioButton && <ViewPortfolioButton />}
      </Flex>
      <StudentArtworkTilesSection
        student={student ?? undefined}
        isLoading={Boolean(studentId) && !student}
        onAddToPortfolio={handleAddToPortfolio}
        onHide={canHideArtwork ? handleHideArtwork : undefined}
      />
    </Flex>
  )
}

/**
 * Lives at the tabs root (outside the per-tab conditionals) so an in-progress
 * upload survives switching tabs, matching the old StudentHistory structure.
 * Only mounted after the first "add artwork" click, so the classes
 * subscription doesn't run for profiles that never open the uploader.
 */
const ArtworkUploadModalHost = ({
  open,
  onClose,
  initialClassId,
}: {
  open: boolean
  onClose: () => void
  initialClassId: Uuid | undefined
}) => {
  const { studentId, student } = useStudentFromForm()

  // Get all class IDs from attendance to populate the class selector
  const classIds = useMemo(() => {
    return uniq(Object.keys(student?.attendance ?? {})).sort()
  }, [student?.attendance])

  const classes = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classIds
  )

  if (!studentId) return null

  return (
    <ArtworkUploadModal
      open={open}
      onClose={onClose}
      studentId={studentId}
      classes={classes ?? []}
      initialClassId={initialClassId}
    />
  )
}

const DocumentsTab = () => {
  const { studentId, student } = useStudentFromForm()

  return (
    <StudentDocumentsSection files={student?.files} studentId={studentId} />
  )
}

/**
 * The full set of student profile tabs as a single component. Callers toggle
 * tabs with the `show*` flags; inactive tabs render `null` children, so they
 * mount nothing and fetch no data until visited. Order: Details, Skill
 * History, Standout Moments, TA Journey, Projects, Artwork, Documents.
 * TA Journey is omitted unless this student has assistant attendance, and a
 * student switch drops a tab that no longer applies.
 */
const StudentProfileTabs = ({
  showDetails = true,
  showSkillHistory = true,
  showProjects = true,
  showArtwork = true,
  showDocuments = true,
  showPortfolioButtonInArtworkTab = false,
}: {
  showDetails?: boolean
  showSkillHistory?: boolean
  showProjects?: boolean
  showArtwork?: boolean
  showDocuments?: boolean
  /** Teacher view keeps View Portfolio inside the Artwork tab; the admin profile renders it above the tab bar instead. */
  showPortfolioButtonInArtworkTab?: boolean
}) => {
  // Details and Documents are admin-side tabs. Roles without students-page
  // access (TEACHER and below) never see them, regardless of flags.
  const canSeeAdminTabs = useHasPermission(
    RESOURCES.STUDENTS_PAGE,
    USER_ACTIONS.READ
  )

  const { studentId, student } = useStudentFromForm()
  const showTaJourney = studentHasAssistantAttendance(
    student?.assistantAttendance
  )

  const visibleTabs = useMemo(() => {
    const tabs: TabKey[] = []
    if (showDetails && canSeeAdminTabs) tabs.push(TAB_KEYS.DETAILS)
    if (showSkillHistory) tabs.push(TAB_KEYS.SKILL_HISTORY)
    tabs.push(TAB_KEYS.STANDOUT_MOMENTS)
    if (showTaJourney) tabs.push(TAB_KEYS.TA_JOURNEY)
    if (showProjects) tabs.push(TAB_KEYS.PROJECTS)
    if (showArtwork) tabs.push(TAB_KEYS.ARTWORK)
    if (showDocuments && canSeeAdminTabs) tabs.push(TAB_KEYS.DOCUMENTS)
    return tabs
  }, [
    showDetails,
    showSkillHistory,
    showTaJourney,
    showProjects,
    showArtwork,
    showDocuments,
    canSeeAdminTabs,
  ])

  const [selectedTab, setSelectedTab] = useState<TabKey | undefined>(undefined)
  const activeTab =
    selectedTab && visibleTabs.includes(selectedTab)
      ? selectedTab
      : visibleTabs[0]

  // Upload modal state lives here (not in ArtworkTab) so an in-progress
  // upload keeps its component mounted while the user switches tabs.
  const [artworkModalOpen, setArtworkModalOpen] = useState(false)
  const [artworkModalMounted, setArtworkModalMounted] = useState(false)
  const [selectedClassIdForUpload, setSelectedClassIdForUpload] = useState<
    Uuid | undefined
  >(undefined)

  const handleAddArtwork = useCallback((classId?: Uuid) => {
    setSelectedClassIdForUpload(classId)
    setArtworkModalMounted(true)
    setArtworkModalOpen(true)
  }, [])

  const handleCloseArtworkModal = useCallback(() => {
    setArtworkModalOpen(false)
    setSelectedClassIdForUpload(undefined)
  }, [])

  const renderTabContent = (key: TabKey): ReactNode => {
    // Only the active tab mounts; hidden tabs subscribe to nothing.
    if (key !== activeTab) return null

    switch (key) {
      case TAB_KEYS.DETAILS:
        return <AddStudentDetails />
      case TAB_KEYS.SKILL_HISTORY:
        return <SkillHistoryTab />
      case TAB_KEYS.STANDOUT_MOMENTS:
        return <StudentStandoutMomentsTab key={studentId ?? "none"} />
      case TAB_KEYS.TA_JOURNEY:
        return <StudentTaJourneyTab key={studentId ?? "none"} />
      case TAB_KEYS.PROJECTS:
        return <ProjectsTab />
      case TAB_KEYS.ARTWORK:
        return (
          <ArtworkTab
            showPortfolioButton={showPortfolioButtonInArtworkTab}
            onAddArtwork={handleAddArtwork}
          />
        )
      case TAB_KEYS.DOCUMENTS:
        return <DocumentsTab />
    }
  }

  if (visibleTabs.length === 0) return null

  return (
    <>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSelectedTab(key as TabKey)}
        items={visibleTabs.map((key) => ({
          key,
          label: TAB_LABELS[key],
          children: renderTabContent(key),
        }))}
      />
      {artworkModalMounted && (
        <ArtworkUploadModalHost
          open={artworkModalOpen}
          onClose={handleCloseArtworkModal}
          initialClassId={selectedClassIdForUpload}
        />
      )}
    </>
  )
}

export default StudentProfileTabs
